// src/App.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "./utils/firebase";
import { setTrialExpiredCallback, updateClubDataCache } from "./utils/firebase";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { UserProvider } from "./contexts/UserContext";
import LoginPage from "./components/LoginPage";
import Menu from "./components/Menu";
import Settings from "./components/Settings";
import ShareView from "./components/ShareView";
import ShareRouter from "./components/ShareRouter";
import SatelliteManager from "./components/satellite/SatelliteManager";
import ClubPitchMap from './components/satellite/ClubPitchMap';
import UnifiedPitchAllocator from './components/allocations/UnifiedPitchAllocator';
import CapacityOutlook from './components/CapacityOutlook';
import SetupWizard from './components/SetupWizard';
import GoogleMapsSatelliteUpload from './components/satellite/GoogleMapsSatelliteUpload';
import ChangeLog from './components/ChangeLog';
import FixtureScheduler from './components/AIComponent/FixtureScheduler';
import Pricing from './components/Pricing';
import OnboardingDashboard from './components/OnboardingDashboard';
import ActiveUsersDashboard from './components/ActiveUsersDashboard';
import EmailVerification from './components/EmailVerification';
import BackupDashboard from './components/BackupDashboard';
import BehaviorAnalyticsDashboard from './components/owner/BehaviorAnalyticsDashboard';
import ClubCreator from './components/ClubCreator';
import OwnerTickerPage from './pages/OwnerTickerPage';
import CoachesHub from './components/CoachesHub';

// Activity tracking for owner dashboard
import { useActivityTracker } from './utils/useActivityTracker';

// Trial system components
import { useTrialStatus } from './hooks/useTrialStatus';
import TrialCountdownBanner from './components/TrialCountdownBanner';
import TrialExpiredModal from './components/TrialExpiredModal';

// Plan limits banner for post-trial upgrade scenarios
import PlanLimitsBanner from './components/PlanLimitsBanner';

// =============================================================================
// CONSOLE CAPTURE - Initialize early to catch all errors
// =============================================================================
import { consoleCapture } from './utils/consoleCapture';
consoleCapture.start();
// =============================================================================

// =============================================================================
// AUTO-LOGOUT CONFIGURATION
// =============================================================================
const SESSION_DURATION = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // Show warning 5 minutes before
const CHECK_INTERVAL = 60 * 1000; // Check every minute
const SESSION_START_KEY = 'pitchallo_session_start';
// =============================================================================

// =============================================================================
// SESSION WARNING MODAL COMPONENT
// =============================================================================
const SessionWarningModal = ({ timeRemaining, onExtend, onLogout }) => {
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 99999,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)',
        textAlign: 'center'
      }}>
        {/* Warning Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          backgroundColor: '#fef3c7',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: '32px'
        }}>
          ⏰
        </div>

        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#1f2937',
          marginTop: 0,
          marginBottom: '12px'
        }}>
          Session Expiring Soon
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          marginBottom: '8px',
          lineHeight: '1.5'
        }}>
          For your security, you'll be automatically logged out due to session timeout.
        </p>

        <p style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#dc2626',
          marginBottom: '24px',
          fontFamily: 'monospace'
        }}>
          {formatTime(timeRemaining || 0)}
        </p>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onLogout}
            style={{
              flex: 1,
              padding: '12px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Log Out Now
          </button>
          <button
            onClick={onExtend}
            style={{
              flex: 1,
              padding: '12px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Protected Route Component - BLOCKS UNVERIFIED EMAILS + TRIAL + AUTO-LOGOUT
// =============================================================================
const PrivateRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasShownModalThisSession, setHasShownModalThisSession] = useState(false);
  const [showBlockedActionModal, setShowBlockedActionModal] = useState(false);
  
  // Resource counts for plan limits checking
  const [resourceCounts, setResourceCounts] = useState({
    pitches: 0,
    teams: 0,
    admins: 0,
    users: 0
  });

  // ===========================================================================
  // AUTO-LOGOUT STATE
  // ===========================================================================
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null);
  const sessionIntervalRef = useRef(null);
  const warningShownRef = useRef(false);

  // ===========================================================================
  // ACTIVITY TRACKING - for Active Users Dashboard
  // ===========================================================================
  useActivityTracker(user?.uid);

  // Get session start time
  const getSessionStart = useCallback(() => {
    const stored = localStorage.getItem(SESSION_START_KEY);
    return stored ? parseInt(stored, 10) : null;
  }, []);

  // Extend session (reset timer)
  const extendSession = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(SESSION_START_KEY, now.toString());
    setShowSessionWarning(false);
    setSessionTimeRemaining(null);
    warningShownRef.current = false;
    console.log('🔄 Session extended at:', new Date(now).toLocaleString());
  }, []);

  // Perform logout due to session expiry
  const performSessionLogout = useCallback(async () => {
    try {
      localStorage.removeItem(SESSION_START_KEY);
      setShowSessionWarning(false);
      warningShownRef.current = false;
      
      await signOut(auth);
      console.log('🔐 Session expired - user logged out');
      
      // Store message for login page
      sessionStorage.setItem('loginError', 'Your session has expired. Please log in again.');
      
    } catch (error) {
      console.error('Auto-logout error:', error);
    }
  }, []);

  // Check session status
  const checkSession = useCallback(() => {
    if (!user) return;

    const sessionStart = getSessionStart();
    
    if (!sessionStart) {
      // No session timestamp - could be legacy login, set one now
      console.log('⚠️ No session timestamp found - creating one');
      localStorage.setItem(SESSION_START_KEY, Date.now().toString());
      return;
    }

    const now = Date.now();
    const elapsed = now - sessionStart;
    const remaining = SESSION_DURATION - elapsed;

    // Session expired
    if (remaining <= 0) {
      console.log('⏰ Session expired!');
      performSessionLogout();
      return;
    }

    // Show warning if within warning window
    if (remaining <= WARNING_BEFORE_LOGOUT && !warningShownRef.current) {
      warningShownRef.current = true;
      setShowSessionWarning(true);
      setSessionTimeRemaining(Math.ceil(remaining / 1000));
      console.log('⚠️ Session warning - expires in', Math.ceil(remaining / 1000), 'seconds');
    }

    // Update countdown if warning is showing
    if (showSessionWarning) {
      setSessionTimeRemaining(Math.ceil(remaining / 1000));
    }
  }, [user, getSessionStart, performSessionLogout, showSessionWarning]);

  // Start session monitoring when user logs in
  useEffect(() => {
    if (user) {
      // Initial check
      checkSession();
      
      // Set up interval
      sessionIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL);
      
      return () => {
        if (sessionIntervalRef.current) {
          clearInterval(sessionIntervalRef.current);
        }
      };
    }
  }, [user, checkSession]);

  // More frequent countdown when warning is showing
  useEffect(() => {
    if (showSessionWarning) {
      const countdownInterval = setInterval(() => {
        setSessionTimeRemaining(prev => {
          if (prev <= 1) {
            performSessionLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(countdownInterval);
    }
  }, [showSessionWarning, performSessionLogout]);

  // Use the trial status hook - original hook only takes clubData
  const trialStatus = useTrialStatus(clubData);
  const { 
    showTrialBanner, 
    daysRemaining,
    isTrialExpired,
    isActive
  } = trialStatus;
  
  // Handle trial modal logic here since original hook doesn't include it
  const showTrialModal = isTrialExpired && !isActive && !hasShownModalThisSession;
  const handleCloseModal = () => setHasShownModalThisSession(true);
  const trialDaysRemaining = daysRemaining;

  // Listen for blocked action events from SubscriptionGate
  useEffect(() => {
    const handleBlockedAction = () => {
      setShowBlockedActionModal(true);
    };

    window.addEventListener('showTrialExpiredModal', handleBlockedAction);
    return () => window.removeEventListener('showTrialExpiredModal', handleBlockedAction);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔐 PrivateRoute: Auth state changed:', currentUser?.email || 'null');
      
      if (!currentUser) {
        console.log('❌ PrivateRoute: No user - redirecting to login');
        setUser(null);
        setClubData(null);
        setLoading(false);
        return;
      }
      
      // Reload user to get fresh email verification status
      await currentUser.reload();
      const refreshedUser = auth.currentUser;
      
      // Block unverified emails
      if (!refreshedUser.emailVerified) {
        console.log('❌ PrivateRoute: Email not verified - blocking access');
        // Sign them out and redirect to login
        await signOut(auth);
        sessionStorage.setItem('loginError', 'Please verify your email address before accessing the app.');
        setUser(null);
        setClubData(null);
        setLoading(false);
        return;
      }
      
      console.log('✅ PrivateRoute: Email verified - loading club subscription data...');
      setUser(refreshedUser);
      
      // Load club data for subscription status
      try {
        const userDoc = await getDoc(doc(db, 'users', refreshedUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const clubId = userData.clubId;
          
          if (clubId) {
            const clubDoc = await getDoc(doc(db, 'clubs', clubId));
            if (clubDoc.exists()) {
              const clubDataFromDb = clubDoc.data();
              setClubData(clubDataFromDb);
              console.log('📊 Club subscription data loaded:', {
                clubId: clubId,
                tier: clubDataFromDb.subscriptionTier,
                status: clubDataFromDb.subscriptionStatus,
                trialEndsAt: clubDataFromDb.trialEndsAt
              });
              
              // Update the cache in firebase.js for SubscriptionGate
              updateClubDataCache(clubDataFromDb);
              
              // Load resource counts for plan limits
              try {
                // Count pitches
                const satelliteDoc = await getDoc(doc(db, 'clubs', clubId, 'settings', 'satellite'));
                const pitchCount = satelliteDoc.exists() 
                  ? (satelliteDoc.data().pitchBoundaries?.length || 0)
                  : 0;
                
                // Count teams
                const teamsDoc = await getDoc(doc(db, 'clubs', clubId, 'settings', 'teams'));
                const teamCount = teamsDoc.exists()
                  ? (teamsDoc.data().teams?.length || 0)
                  : 0;
                
                // Count users (admins vs regular)
                const usersQuery = query(
                  collection(db, 'users'),
                  where('clubId', '==', clubId)
                );
                const usersSnapshot = await getDocs(usersQuery);
                let adminCount = 0;
                let userCount = 0;
                usersSnapshot.forEach(doc => {
                  const role = doc.data().role;
                  if (role === 'admin') adminCount++;
                  else userCount++;
                });
                
                setResourceCounts({
                  pitches: pitchCount,
                  teams: teamCount,
                  admins: adminCount,
                  users: userCount
                });
                
                console.log('📊 Resource counts loaded:', {
                  pitches: pitchCount,
                  teams: teamCount,
                  admins: adminCount,
                  users: userCount
                });
              } catch (countError) {
                console.error('Error loading resource counts:', countError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading club data:', error);
      }
      
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Register the trial expired callback for SubscriptionGate to use
  useEffect(() => {
    setTrialExpiredCallback(() => {
      setShowBlockedActionModal(true);
    });
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Determine if we should show plan limits banner
  // Show when: trial expired AND on a tier with limits that are being approached/exceeded
  const shouldShowPlanLimits = clubData?.subscriptionStatus === 'expired' || 
    (clubData?.subscriptionStatus === 'active' && clubData?.subscriptionTier !== 'premium');

  return (
    <>
      {/* Session Warning Modal - Highest priority */}
      {showSessionWarning && (
        <SessionWarningModal
          timeRemaining={sessionTimeRemaining}
          onExtend={extendSession}
          onLogout={performSessionLogout}
        />
      )}
      
      {/* Trial Countdown Banner - Shows during trial period */}
      {showTrialBanner && !showSessionWarning && (
        <TrialCountdownBanner daysRemaining={trialDaysRemaining} />
      )}
      
      {/* Expired/Free Account Banner - Shows for expired trials */}
      {isTrialExpired && !showTrialBanner && !showSessionWarning && (
        <div style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          flexWrap: 'wrap',
          gap: '12px',
          borderBottom: '2px solid #ef4444'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px' }}>Your trial has expired</div>
              <div style={{ fontSize: '13px', color: '#b91c1c' }}>
                Upgrade to continue creating and editing allocations
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => window.location.href = '/pricing'}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#991b1b',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              View Plans
            </button>
            <button
              onClick={() => window.location.href = '/pricing'}
              style={{
                padding: '8px 16px',
                backgroundColor: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              Upgrade
            </button>
          </div>
        </div>
      )}
      
      {/* Plan Limits Banner - Shows for active subscribers over limits */}
      {!isTrialExpired && shouldShowPlanLimits && !showTrialBanner && !showSessionWarning && (
        <PlanLimitsBanner 
          subscriptionTier={clubData?.subscriptionTier || 'free'}
          subscriptionStatus={clubData?.subscriptionStatus || 'expired'}
          currentPitches={resourceCounts.pitches}
          currentTeams={resourceCounts.teams}
          currentAdmins={resourceCounts.admins}
          currentUsers={resourceCounts.users}
        />
      )}
      
      {/* Trial Expired Modal - Auto-shown once per session */}
      {showTrialModal && !showSessionWarning && (
        <TrialExpiredModal 
          onClose={handleCloseModal}
          clubData={clubData}
        />
      )}
      
      {/* Blocked Action Modal - Shown when user tries to access gated feature */}
      {showBlockedActionModal && !showTrialModal && !showSessionWarning && (
        <TrialExpiredModal 
          onClose={() => setShowBlockedActionModal(false)}
          clubData={clubData}
          isBlockedAction={true}
        />
      )}
      
      {children}
    </>
  );
};

// =============================================================================
// Public Route Component - Redirects to menu if already logged in with verified email
// =============================================================================
const PublicRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔐 PublicRoute: Auth state changed:', currentUser?.email || 'null');
      
      if (!currentUser) {
        console.log('✅ PublicRoute: No user - showing login page');
        setUser(null);
        setLoading(false);
        return;
      }
      
      // CRITICAL: Block navigation if email not verified
      if (currentUser && !currentUser.emailVerified) {
        console.log('❌ PublicRoute: Email not verified - staying on login page');
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Only set user if email is verified
      console.log('✅ PublicRoute: Email verified or no user - proceeding normally');
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  return user ? <Navigate to="/menu" replace /> : children;
};

// Wrapper component for Google Maps Satellite Upload
const SatelliteUploadPage = () => {
  const handleImageSaved = async (imageData) => {
    try {
      console.log('Satellite image captured:', imageData);
      
      if (imageData.skipRedirect) {
        return;
      }
      
      window.location.href = '/club-pitch-map';
      
    } catch (error) {
      console.error('Error saving satellite image:', error);
      alert('Failed to save satellite image. Please try again.');
    }
  };

  const handleError = (error) => {
    console.error('Satellite capture error:', error);
    alert('Error capturing satellite image. Please try again.');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <div style={{ 
        padding: '20px',
        maxWidth: '1200px', 
        margin: '0 auto' 
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
          }}>
            Capture Club Satellite Image
          </h1>
          
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '12px 20px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back
          </button>
        </div>

        <GoogleMapsSatelliteUpload
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          onImageSaved={handleImageSaved}
          onError={handleError}
          initialZoom={18}
        />
      </div>
    </div>
  );
};

function App() {
  return (
    <UserProvider>
      <Router>
        <Routes>
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />

          {/* NEW share link routes - MUST come before legacy routes */}
          <Route 
            path="/share/:clubId/:dateOrToday/:team" 
            element={<ShareRouter />} 
          />
          
          <Route 
            path="/share/:clubId/:dateOrToday" 
            element={<ShareRouter />} 
          />
          
          {/* Legacy share routes - keep for backward compatibility */}
          <Route 
            path="/share/:shareId/:pitchId" 
            element={<ShareView />} 
          />
          
          <Route path="/coaches-hub" 
            element={<CoachesHub />} 
            />
          
          <Route 
            path="/share/:shareId" 
            element={<ShareView />} 
          />

          <Route 
            path="/__/auth/action" 
            element={<EmailVerification />} 
          />
          
          <Route
            path="/menu"
            element={
              <PrivateRoute>
                <Menu />
              </PrivateRoute>
            }
          />

          <Route
            path="/club-pitch-map"
            element={
              <PrivateRoute>
                <ClubPitchMap />
              </PrivateRoute>
            }
          />
         
          <Route
            path="/fixture-scheduler"
            element={
              <PrivateRoute>
                <FixtureScheduler />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/allocator/:pitchId"
            element={
              <PrivateRoute>
                <UnifiedPitchAllocator />
              </PrivateRoute>
            }
          />

          <Route
            path="/capacity-outlook"
            element={
              <PrivateRoute>
                <CapacityOutlook />
              </PrivateRoute>
            }
          />

          <Route
            path="/satellite"
            element={
              <PrivateRoute>
                <SatelliteManager />
              </PrivateRoute>
            }
          />

          <Route 
            path="/satellite-upload" 
            element={
              <PrivateRoute>
                <SatelliteUploadPage />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/changelog" 
            element={
              <PrivateRoute>
                <ChangeLog />
              </PrivateRoute>
            } 
          />
          
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />

          <Route 
            path="/setup-wizard" 
            element={
              <PrivateRoute>
                <SetupWizard />
              </PrivateRoute>
            } 
          />

          <Route 
            path="/pricing" 
            element={
              <PrivateRoute>
                <Pricing />
              </PrivateRoute>
            } 
          />

          {/* Secret owner-only dashboards - handle own auth internally */}
          <Route 
            path="/owner-9x7kq2m" 
            element={<OnboardingDashboard />} 
          />

          <Route 
            path="/owner-active-users" 
            element={<ActiveUsersDashboard />} 
          />

          <Route path="/owner-backup-7k3m" element={<BackupDashboard />} />

          <Route path="/owner-behavior-8n4p" element={<BehaviorAnalyticsDashboard />} />

          <Route path="/owner-club-creator" element={<ClubCreator />} />

          <Route path="/owner-ticker" element={<OwnerTickerPage />} />
          
          <Route 
            path="/" 
            element={<Navigate to="/menu" replace />}
          />

          <Route 
            path="*" 
            element={<Navigate to="/menu" replace />}
          />
        </Routes>
      </Router>
    </UserProvider>
  );
}

export default App;
